import { useMutation } from '@apollo/client';
import {
  Button,
  Form,
  Input,
  Modal,
  type ModalProps,
  Tabs,
  type TabsProps,
} from 'antd';
import { isEmpty, isString } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import {
  MAX_COMMENT_CATEGORY_CHAR_LENGTH,
  SHOULD_GO_TO_NEXT_PAGE_AFTER_FEEDBACK_SUBMIT,
} from '../../../../common/constants';
import { titleCase } from '../../../../common/utils';
import FeedbackVoteFormItem from '../../../../components/FeedbackVoteFormItem';
import LoaderComponent from '../../../../components/LoaderComponent';
import useAutoFocusInput from '../../../../hooks/use-auto-focus-input';
import type { AppContextType } from '../../../../types/appContext.type';
import { VoteEnum } from '../../../../types/common.type';
import { CREATE_FEEDBACK } from '../../graphql/mutations';
import MarkdownViewer from './MarkdownViewer';
import type { ReligiousTransparencyAnalysesData } from './ReligiousTransparencyAnalysis';
import BiasDetectedForm, {
  type BiasDetectedFormValues,
} from './RT/BiasDetectedForm';
import CostTokenDetails from './RT/CostTokenDetails';
import FinalReviewForm from './RT/FInalReviewForm';
import SignalDetectionForm, {
  type SignalDetectionFormValues,
} from './RT/SignalDetectionForm';

const { TextArea } = Input;

type FinalReviewFormValues = {
  vote?: VoteEnum;
  religiousClientComments?: string;
  religiousDraftStanceByReligion?: string[] | null | Record<string, string>;
  religiousReligionKeywordsFound?: boolean | null;
  religiousKeywordList?: string[] | null;
  religiousReligionsNamed?: string[] | null;
  religiousFirstOrSecondPersonPronouns?: boolean | null;
  religiousOverallBiasScore?: number | null;
  religiousReasonNote?: string | null;
  religiousMiracleMentionedHow?: string[] | null;
  religiousExplicitOrImplicit?: string[] | null;
  religiousWhoSaysIt?: string[] | null;
  religiousTone?: string[] | null;
  religiousToneConfidence?: number | null;
  religiousReligiousSlangFound?: boolean | null;
  religiousDownvote?: boolean | null;
  religiousUpvote?: boolean | null;
  religiousSlangList?: string[] | null;
  religiousHistoricSlursFound?: boolean | null;
  religiousHistoricSlurList?: string[] | null;
  religiousMetaphoricReligiousTermFound?: boolean | null;
  religiousMetaphoricReligiousTermList?: string[] | null;
  religiousDisrespectTriggerList?: string[] | null;
  religiousDisrespectedReligions?: string[] | null;
  religiousComparisonFrameDetected?: boolean | null;
  religiousComparedReligions?: string[] | null;
  religiousDoctrineClaimType?: string[] | null;
  religiousFramingStrength?: string[] | null;
  religiousDisrespectTowardsReligion?: boolean | null;
};

type FormType = FinalReviewFormValues & {
  vote?: VoteEnum;
  religiousClientComments?: string;
  religiousDraftStanceByReligion?: string[] | null | Record<string, string>;
  religiousReligionKeywordsFound?: boolean | null;
  religiousKeywordList?: string[] | null;
  religiousReligionsNamed?: string[] | null;
  religiousFirstOrSecondPersonPronouns?: boolean | null;
  religiousOverallBiasScore?: number | null;
  religiousReasonNote?: string | null;
  religiousMiracleMentionedHow?: string[] | null;
  religiousExplicitOrImplicit?: string[] | null;
  religiousWhoSaysIt?: string[] | null;
  religiousTone?: string[] | null;
  religiousToneConfidence?: number | null;
  religiousReligiousSlangFound?: boolean | null;
  religiousDownvote?: boolean | null;
  religiousUpvote?: boolean | null;
  religiousSlangList?: string[] | null;
  religiousHistoricSlursFound?: boolean | null;
  religiousHistoricSlurList?: string[] | null;
  religiousMetaphoricReligiousTermFound?: boolean | null;
  religiousMetaphoricReligiousTermList?: string[] | null;
  religiousDisrespectTriggerList?: string[] | null;
  religiousDisrespectedReligions?: string[] | null;
  religiousComparisonFrameDetected?: boolean | null;
  religiousComparedReligions?: string[] | null;
  religiousDoctrineClaimType?: string[] | null;
  religiousFramingStrength?: string[] | null;
  religiousDisrespectTowardsReligion?: boolean | null;
};

export type FormValues =
  | FormType
  | SignalDetectionFormValues
  | BiasDetectedFormValues
  | FinalReviewFormValues;

type SubSectionDetailsModalProps = {
  modalOpen: boolean;
  closeModal: () => void;
  selectReligiousTransparencyData:
    | ReligiousTransparencyAnalysesData
    | undefined;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  activeIndex: number;
  loading?: boolean;
  changePage: (page: number) => void;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    listDataCount: number;
  };
  modifyReligiousTransparencyData: (
    documentId: string,
    value: FormValues,
  ) => void;
} & ModalProps;

const ReligiousTransparencyDetailsModal: React.FC<
  SubSectionDetailsModalProps
> = (props) => {
  const {
    modalOpen,
    closeModal,
    selectReligiousTransparencyData,
    setActiveIndex,
    activeIndex,
    loading: dataLoading = false,
    pagination,
    changePage,
    modifyReligiousTransparencyData,
  } = props;

  const {
    state: { currentUser },
  } = useContext(AppContext) as AppContextType;

  const [activeTab, setActiveTab] = useState<
    'signalDetection' | 'biasDetected' | 'finalReview' | 'costTokens'
  >('signalDetection');
  const [submitBtnDisabled, setIsSubmitBtnDisabled] = useState(true);

  const [form] = Form.useForm<FormType>();
  const [signalDetectionFormInstance] =
    Form.useForm<SignalDetectionFormValues>();
  const [finalReviewFormInstance] = Form.useForm<FinalReviewFormValues>();
  const [biasDetectedFormInstance] = Form.useForm<BiasDetectedFormValues>();

  const vote = Form.useWatch('vote', form) ?? VoteEnum.NoVote;
  const inputRef = useAutoFocusInput(vote === VoteEnum.DownVote);
  const comment = Form.useWatch('religiousClientComments', form) ?? 0;
  const feedbackData = selectReligiousTransparencyData?.subSection?.feedback;

  const isNextPage = () => {
    const dataCount = pagination.listDataCount;
    form.resetFields();
    setIsSubmitBtnDisabled(true);
    const isLastItemOnPage = dataCount === activeIndex + 1;
    if (isLastItemOnPage) {
      if (
        dataCount < pagination?.pageSize ||
        dataCount * pagination?.page === pagination?.total
      ) {
        closeModal();
        setActiveTab('signalDetection');
      } else {
        setActiveTab('signalDetection');
        changePage(pagination?.page + 1);
        setActiveIndex(0);
      }
      return;
    }
    setActiveTab('signalDetection');
    setActiveIndex((prev) => prev + 1);
  };

  const [createFeedback, { loading }] = useMutation(CREATE_FEEDBACK, {
    onCompleted() {
      if (SHOULD_GO_TO_NEXT_PAGE_AFTER_FEEDBACK_SUBMIT) isNextPage();
    },
    onError() {},
  });

  const onFinishCombined = async (formValues: FormValues) => {
    const {
      vote,
      religiousDraftStanceByReligion,
      religiousClientComments,
      religiousReligionKeywordsFound,
      religiousKeywordList,
      religiousReligionsNamed,
      religiousFirstOrSecondPersonPronouns,
      religiousOverallBiasScore,
      religiousReasonNote,
      religiousMiracleMentionedHow,
      religiousExplicitOrImplicit,
      religiousWhoSaysIt,
      religiousTone,
      religiousToneConfidence,
      religiousReligiousSlangFound,
      religiousDownvote,
      religiousUpvote,
      religiousSlangList,
      religiousHistoricSlursFound,
      religiousHistoricSlurList,
      religiousMetaphoricReligiousTermFound,
      religiousMetaphoricReligiousTermList,
      religiousDisrespectTriggerList,
      religiousDisrespectedReligions,
      religiousComparisonFrameDetected,
      religiousComparedReligions,
      religiousDoctrineClaimType,
      religiousFramingStrength,
      religiousDisrespectTowardsReligion,
      ...rest
    } = formValues;
    const updatedReligiousDraftStanceByReligion = (
      Array.isArray(religiousDraftStanceByReligion)
        ? religiousDraftStanceByReligion
        : []
    ).reduce<Record<string, string>>((acc, str) => {
      if (typeof str === 'string' && str.includes(':')) {
        const [religion, stance] = str.split(':').map((s: string) => s.trim());
        if (religion) acc[religion] = stance || '';
      } else if (typeof str === 'string' && str.trim()) {
        (acc as Record<string, string>)[str.trim()] = '';
      }
      return acc;
    }, {});

    const updatedFields = {
      ...rest,
      religiousClientComments,
      religiousReligionKeywordsFound,
      religiousKeywordList: religiousKeywordList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousReligionsNamed: religiousReligionsNamed?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousFirstOrSecondPersonPronouns,
      religiousOverallBiasScore,
      religiousReasonNote,
      religiousMiracleMentionedHow: religiousMiracleMentionedHow?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousExplicitOrImplicit: religiousExplicitOrImplicit?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousWhoSaysIt: religiousWhoSaysIt?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousTone: religiousTone?.map((val) => val.split('__')[0] ?? ''),
      religiousToneConfidence,
      religiousReligiousSlangFound,
      religiousDownvote,
      religiousUpvote,
      religiousSlangList: religiousSlangList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousHistoricSlursFound,
      religiousHistoricSlurList: religiousHistoricSlurList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousMetaphoricReligiousTermFound,
      religiousMetaphoricReligiousTermList:
        religiousMetaphoricReligiousTermList?.map(
          (val) => val.split('__')[0] ?? '',
        ),
      religiousDisrespectTriggerList: religiousDisrespectTriggerList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousDisrespectedReligions: religiousDisrespectedReligions?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousComparisonFrameDetected,
      religiousComparedReligions: religiousComparedReligions?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousDoctrineClaimType: religiousDoctrineClaimType?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousFramingStrength: religiousFramingStrength?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      religiousDisrespectTowardsReligion,
    };

    const { data } = await createFeedback({
      variables: {
        data: {
          ...updatedFields,
          religiousDownvote: vote === VoteEnum.DownVote,
          religiousUpvote: vote === VoteEnum.UpVote,
          religiousToneConfidence: religiousToneConfidence
            ? Number(religiousToneConfidence.toFixed(2))
            : null,
          userId: currentUser?.id,
          subSection: selectReligiousTransparencyData?.subSection?.documentId,
          religiousDraftStanceByReligion: updatedReligiousDraftStanceByReligion,
        },
      },
    });

    if (data?.createFeedback) {
      modifyReligiousTransparencyData(
        selectReligiousTransparencyData?.documentId ?? '',
        {
          ...formValues,
          religiousDownvote: vote === VoteEnum.DownVote,
          religiousUpvote: vote === VoteEnum.UpVote,
          religiousClientComments: formValues.religiousClientComments ?? '',
          religiousToneConfidence: formValues.religiousToneConfidence
            ? Number(formValues.religiousToneConfidence.toFixed(2))
            : null,
          religiousDraftStanceByReligion: updatedReligiousDraftStanceByReligion,
        },
      );
    }
  };

  const handleSubmitBothForms = async () => {
    try {
      const signalDetectionValues =
        await signalDetectionFormInstance.validateFields();
      const commentFormValues = await form.validateFields();
      const biasDetectedValues =
        await biasDetectedFormInstance.validateFields();
      const finalReviewValues = await finalReviewFormInstance.validateFields();

      const allValues: FormValues = {
        ...signalDetectionValues,
        ...commentFormValues,
        ...biasDetectedValues,
        ...finalReviewValues,
      };

      onFinishCombined(allValues);
    } catch (error) {
      console.error(error);
    }
  };

  const items: TabsProps['items'] = [
    {
      key: 'signalDetection',
      label: 'Signal Detection',
      children: (
        <SignalDetectionForm
          form={signalDetectionFormInstance}
          setIsSubmitBtnDisabled={setIsSubmitBtnDisabled}
          selectReligiousTransparencyData={selectReligiousTransparencyData}
        />
      ),
    },
    {
      key: 'biasDetected',
      label: 'Bias Detected',
      children: (
        <BiasDetectedForm
          form={biasDetectedFormInstance}
          setIsSubmitBtnDisabled={setIsSubmitBtnDisabled}
          selectReligiousTransparencyData={selectReligiousTransparencyData}
        />
      ),
    },
    {
      key: 'finalReview',
      label: 'Final Review',
      children: (
        <FinalReviewForm
          form={finalReviewFormInstance}
          setIsSubmitBtnDisabled={setIsSubmitBtnDisabled}
          selectReligiousTransparencyData={selectReligiousTransparencyData}
        />
      ),
    },
    {
      key: 'costTokens',
      label: 'Cost & Tokens',
      children: (
        <CostTokenDetails
          selectReligiousTransparencyData={selectReligiousTransparencyData}
        />
      ),
    },
  ];

  useEffect(() => {
    const getReligiousDraftStanceByReligion = Object.entries(
      feedbackData?.religiousDraftStanceByReligion ??
        selectReligiousTransparencyData?.draftStanceByReligion ??
        {},
    )
      .filter(([_, stance]) => isString(stance))
      .map(([religion, stance]) => ({
        [titleCase(religion as string)]: stance as string,
      }));
    signalDetectionFormInstance?.setFieldsValue({
      religiousReligionKeywordsFound:
        feedbackData?.religiousReligionKeywordsFound ??
        selectReligiousTransparencyData?.religionKeywordsFound,
      religiousKeywordList:
        feedbackData?.religiousKeywordList ??
        selectReligiousTransparencyData?.keywordList,
      religiousReligionsNamed:
        feedbackData?.religiousReligionsNamed ??
        selectReligiousTransparencyData?.religionsNamed,
      religiousFirstOrSecondPersonPronouns:
        feedbackData?.religiousFirstOrSecondPersonPronouns ??
        selectReligiousTransparencyData?.firstOrSecondPersonPronouns,
    });

    finalReviewFormInstance?.setFieldsValue({
      religiousOverallBiasScore:
        feedbackData?.religiousOverallBiasScore ??
        selectReligiousTransparencyData?.overallBiasScore,
      religiousReasonNote:
        feedbackData?.religiousReasonNote ??
        selectReligiousTransparencyData?.reasonNote,
    });

    biasDetectedFormInstance?.setFieldsValue({
      religiousMiracleMentionedHow:
        feedbackData?.religiousMiracleMentionedHow ??
        selectReligiousTransparencyData?.miracleMentionedHow,
      religiousExplicitOrImplicit:
        feedbackData?.religiousExplicitOrImplicit ??
        selectReligiousTransparencyData?.explicitOrImplicit,
      religiousWhoSaysIt:
        feedbackData?.religiousWhoSaysIt ??
        selectReligiousTransparencyData?.whoSaysIt,
      religiousTone:
        feedbackData?.religiousTone ?? selectReligiousTransparencyData?.tone,
      religiousToneConfidence:
        feedbackData?.religiousToneConfidence ??
        selectReligiousTransparencyData?.toneConfidence,
      religiousReligiousSlangFound:
        feedbackData?.religiousReligiousSlangFound ??
        selectReligiousTransparencyData?.religiousSlangFound,
      religiousHistoricSlursFound:
        feedbackData?.religiousHistoricSlursFound ??
        selectReligiousTransparencyData?.historicSlursFound,
      religiousHistoricSlurList:
        feedbackData?.religiousHistoricSlurList ??
        selectReligiousTransparencyData?.historicSlurList,
      religiousSlangList:
        feedbackData?.religiousReligiousSlangList ??
        selectReligiousTransparencyData?.religiousSlangList,
      religiousMetaphoricReligiousTermList:
        feedbackData?.religiousMetaphoricReligiousTermList ??
        selectReligiousTransparencyData?.metaphoricReligiousTermList,
      religiousDisrespectTriggerList:
        feedbackData?.religiousDisrespectTriggerList ??
        selectReligiousTransparencyData?.disrespectTriggerList,
      religiousDisrespectedReligions:
        feedbackData?.religiousDisrespectedReligions ??
        selectReligiousTransparencyData?.disrespectedReligions,
      religiousComparisonFrameDetected:
        feedbackData?.religiousComparisonFrameDetected ??
        selectReligiousTransparencyData?.comparisonFrameDetected,
      religiousComparedReligions:
        feedbackData?.religiousComparedReligions ??
        selectReligiousTransparencyData?.comparedReligions,
      religiousDoctrineClaimType:
        feedbackData?.religiousDoctrineClaimType ??
        selectReligiousTransparencyData?.doctrineClaimType,
      religiousFramingStrength:
        feedbackData?.religiousFramingStrength ??
        selectReligiousTransparencyData?.framingStrength,
      religiousDraftStanceByReligion: getReligiousDraftStanceByReligion.map(
        (obj) => {
          const entry = Object.entries(obj)[0];
          if (!entry) return '';
          const [religion, stance] = entry;
          return `${religion}: ${stance}`;
        },
      ),
      religiousDisrespectTowardsReligion:
        feedbackData?.religiousDisrespectTowardsReligion ??
        selectReligiousTransparencyData?.disrespectTowardsReligion,
    });

    form.setFieldsValue({
      vote: feedbackData?.religiousUpvote
        ? VoteEnum.UpVote
        : feedbackData?.religiousDownvote
          ? VoteEnum.DownVote
          : VoteEnum.NoVote,
      religiousClientComments:
        !isEmpty(feedbackData?.religiousClientComments) &&
        feedbackData?.religiousClientComments !== null
          ? feedbackData?.religiousClientComments
          : `I expected ... \n I experienced ...`,
    });
  }, [selectReligiousTransparencyData, activeIndex]);

  const handleTabChange = (key: string) => {
    const newTab = key as
      | 'signalDetection'
      | 'biasDetected'
      | 'finalReview'
      | 'costTokens';
    setActiveTab(newTab);
  };

  return (
    <Modal
      open={modalOpen}
      onCancel={() => {
        closeModal();
        setActiveTab('signalDetection');
      }}
      centered
      onOk={() => setActiveIndex((prev) => prev + 1)}
      title={
        <div className="d-flex align-center section-modal title">
          <h5>Religious Transparency Analysis Report</h5>
          <div className="section-modal content">
            <span>
              <strong>Overall Bias Score</strong>:{' '}
              <span className="content-values">
                {selectReligiousTransparencyData?.overallBiasScore}
              </span>
            </span>
            <span>
              <strong>Page #</strong>:{' '}
              <span className="content-values">
                {selectReligiousTransparencyData?.subSection?.pageNumber}
              </span>
            </span>
            <span>
              <strong>Para #</strong>:{' '}
              <span className="content-values">
                {selectReligiousTransparencyData?.subSection?.paragraphNumber}
              </span>
            </span>
          </div>
        </div>
      }
      width="auto"
      okText="Next"
      footer={null}
      cancelText="Previous"
      className="sub-section-modal"
      {...props}
    >
      {dataLoading ? (
        <LoaderComponent />
      ) : (
        <div className="modal-book-wrapper">
          <div className="modal-book-wrapper-image p-0 paragraph-section">
            <fieldset>
              <legend>Input (Paragraph)</legend>
              <MarkdownViewer
                content={
                  selectReligiousTransparencyData?.subSection?.paragraph ?? ''
                }
                className="modal-markdown-scroll"
              />
            </fieldset>
          </div>
          <div className="modal-book-wrapper-content content-section content-section-flex">
            <div>
              <Tabs
                items={items.map((item) => ({
                  ...item,
                  children: (
                    <div className="modal-tab-content-scroll-section">
                      {item.children}
                    </div>
                  ),
                }))}
                activeKey={activeTab}
                defaultActiveKey="1"
                onChange={handleTabChange}
              />
            </div>
            <div className="modal-category-section category-section-fixed">
              <Form
                layout="horizontal"
                form={form}
                className="mt-8"
                onValuesChange={() => setIsSubmitBtnDisabled(false)}
                initialValues={{
                  vote: VoteEnum.NoVote,
                  suggestedRating: null,
                  suggestedCategory: null,
                  religiousClientComments: `I expected ... \n I experienced ...`,
                }}
              >
                <FeedbackVoteFormItem
                  vote={vote}
                  name="vote"
                  onChange={(_vote) => {
                    const isUpvote = _vote === VoteEnum.UpVote;

                    form.setFieldValue(
                      'religiousClientComments',
                      isUpvote === feedbackData?.religiousUpvote
                        ? !isEmpty(feedbackData?.religiousClientComments) &&
                          feedbackData?.religiousClientComments !== null
                          ? feedbackData?.religiousClientComments
                          : `I expected ... \n I experienced ...`
                        : `I expected ... \n I experienced ...`,
                    );
                    form.setFieldValue('vote', _vote);
                  }}
                  label="Feedback"
                />
                {vote !== VoteEnum.NoVote && (
                  <Form.Item
                    name="religiousClientComments"
                    rules={[
                      {
                        max: MAX_COMMENT_CATEGORY_CHAR_LENGTH,
                        message: `Comment cannot exceed ${MAX_COMMENT_CATEGORY_CHAR_LENGTH} characters`,
                      },
                    ]}
                  >
                    <TextArea
                      className="feedback-textarea"
                      placeholder="Enter comment"
                      ref={inputRef}
                    />
                  </Form.Item>
                )}
                <Form.Item>
                  <Button
                    disabled={
                      loading ||
                      submitBtnDisabled ||
                      (vote &&
                        vote === VoteEnum.DownVote &&
                        (comment ?? '') === '')
                    }
                    onClick={handleSubmitBothForms}
                    loading={loading}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Form>
              <div className="d-flex gap-8 justify-end">
                <Button
                  onClick={() => {
                    if (activeIndex === 0) {
                      changePage(pagination?.page - 1);
                      form.resetFields();
                      setActiveIndex(pagination.pageSize - 1);
                      setIsSubmitBtnDisabled(true);
                      return;
                    }
                    setActiveIndex((prev) => prev - 1);
                    form.resetFields();
                  }}
                  disabled={
                    loading || (pagination.page === 1 && activeIndex === 0)
                  }
                >
                  Previous
                </Button>
                <Button
                  onClick={() => {
                    isNextPage();
                  }}
                  disabled={loading}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ReligiousTransparencyDetailsModal;
